import { DeleteOutlined } from "@ant-design/icons";
import { Button, Col, DatePicker, Input, InputNumber, Row, Select, TableColumnType } from "antd";
import { AdvertiserSelectSearchable } from "components/UI/Selects/AdvertiserSelectSearchable/AdvertiserSelectSearchable.tsx";
import { CascadeSelectSearchable } from "components/UI/Selects/CascadeSelectSearchable/CascadeSelectSearchable.tsx";
import { CategorySelectSearchable } from "components/UI/Selects/CategorySelectSearchable/CategorySelectSearchable.tsx";
import { useConversionTypeSelectOptions } from "components/UI/Selects/ConversionTypeSelectSearchable/useConversionTypeSelectOptions.ts";
import { CountrySelectSearchable } from "components/UI/Selects/CountrySelectSearchable/CountrySelectSearchable.tsx";
import { FormStructureSelectSearchable } from "components/UI/Selects/FormStructureSelectSearchable/FormStructureSelectSearchable.tsx";
import { LabelSelectSearchable } from "components/UI/Selects/LabelSelectSearchable/LabelSelectSearchable.tsx";
import { OfferSelectSearchable } from "components/UI/Selects/OfferSelectSearchable/OfferSelectSearchable.tsx";
import { PartnerSelectSearchable } from "components/UI/Selects/PartnerSelectSearchable/PartnerSelectSearchable.tsx";
import { useSourceSelectOptions } from "components/UI/Selects/SourceSelectSearchable/useSourceSelectOptions.ts";
import { StepTypeSelectSearchable } from "components/UI/Selects/StepTypeSelectSearchable/StepTypeSelectSearchable.tsx";
import { FILTER_TYPES } from "components/UI/Table/constants/filter";
import { BooleanFilter, FilterRule, FilterType } from "components/UI/Table/types/filter";
import dayjs from "dayjs";
import { useFilterTranslations } from "hooks/useFilterTranslations";
import { DATE_FORMAT, DATE_FORMAT_WITH_TIME_AND_SECONDS } from "localConstants";
import { isValidElement } from "react";
import { SearchFilterEnum } from "types/enums";
import { handleShowSearchFilterOption } from "utils/handleShowSearchFilterOption";

const { RangePicker } = DatePicker;

interface FilterItemProps<T> {
    filter: FilterRule;
    updateFilter: (id: number, key: keyof FilterRule, value: unknown) => void;
    removeFilter: (id: number) => void;
    availableFilterColumns: TableColumnType<T>[];
}

export const FilterItem = <T,>({ availableFilterColumns, filter, updateFilter, removeFilter }: FilterItemProps<T>) => {
    const { type } = filter.filterType;
    const filterTranslations = useFilterTranslations();
    const isEntitySelect =
        type === FILTER_TYPES.offerSelect.type ||
        type === FILTER_TYPES.partnerSelect.type ||
        type === FILTER_TYPES.advertiserSelect.type ||
        type === FILTER_TYPES.cascadeSelect.type;

    const isConversionTypeFilter =
        type === FILTER_TYPES.conversionStateTypeSelect.type ||
        type === FILTER_TYPES.conversionStateTypeMultiSelect.type;

    const { options: conversionSourceOptions } = useSourceSelectOptions();
    const { options: conversionTypeOptions } = useConversionTypeSelectOptions(isConversionTypeFilter);

    const placeholder = (filter.filterType as FilterType).placeholder ?? filterTranslations.placeholders[type];

    const renderValueInput = () => {
        const isValuelessOperator =
            filter.operator === SearchFilterEnum.Null || filter.operator === SearchFilterEnum.NotNull;

        if (isValuelessOperator) {
            return null;
        }

        const isRange = filter.operator === SearchFilterEnum.Between || filter.operator === SearchFilterEnum.NotBetween;

        if (isRange) {
            const [start, end] = Array.isArray(filter.value) ? filter.value : [null, null];

            // Number range: use two InputNumber fields
            if (type === FILTER_TYPES.number.type) {
                return (
                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                        <InputNumber
                            style={{ flex: 1 }}
                            placeholder={filterTranslations.options.from}
                            value={start as number | null}
                            onChange={(v) => updateFilter(filter.id, "value", [v, end])}
                        />
                        <InputNumber
                            style={{ flex: 1 }}
                            placeholder={filterTranslations.options.to}
                            value={end as number | null}
                            onChange={(v) => updateFilter(filter.id, "value", [start, v])}
                        />
                    </div>
                );
            }

            // Date/datetime range: use RangePicker
            return (
                <RangePicker
                    className="w-full"
                    showTime={type === "datetime"}
                    placeholder={[filterTranslations.options.from, filterTranslations.options.to]}
                    value={[start ? dayjs(start as string) : null, end ? dayjs(end as string) : null]}
                    onChange={(dates) => {
                        const [s, e] = dates ?? [null, null];
                        const fmt = (d: dayjs.Dayjs | null) => (d ? d.format(DATE_FORMAT_WITH_TIME_AND_SECONDS) : null);
                        // if it's a pure date filter, end should be endOf("day")
                        const endValue =
                            type === FILTER_TYPES.date.type && e
                                ? e.endOf("day").format(DATE_FORMAT_WITH_TIME_AND_SECONDS)
                                : fmt(e);
                        updateFilter(filter.id, "value", [fmt(s), endValue]);
                    }}
                    allowClear
                />
            );
        }

        switch (type) {
            case FILTER_TYPES.string.type:
                return (
                    <Input
                        placeholder={placeholder}
                        value={filter.value as string}
                        onChange={(e) => updateFilter(filter.id, "value", e.target.value)}
                    />
                );

            case FILTER_TYPES.number.type:
                return (
                    <InputNumber
                        style={{ width: "100%" }}
                        placeholder={placeholder}
                        value={filter.value as number}
                        onChange={(v: number | null) => updateFilter(filter.id, "value", v ?? 0)}
                    />
                );

            case FILTER_TYPES.date.type:
                return (
                    <DatePicker
                        placeholder={placeholder}
                        className="w-full"
                        value={filter.value ? dayjs(filter.value as string) : undefined}
                        onChange={(d) => updateFilter(filter.id, "value", d?.format(DATE_FORMAT) ?? null)}
                    />
                );

            case FILTER_TYPES.datetime.type:
                return (
                    <DatePicker
                        showTime
                        placeholder={placeholder}
                        value={filter.value ? dayjs(filter.value as string) : undefined}
                        onChange={(d) =>
                            updateFilter(filter.id, "value", d?.format(DATE_FORMAT_WITH_TIME_AND_SECONDS) ?? null)
                        }
                        className="w-full"
                    />
                );

            case FILTER_TYPES.select.type:
                if (filter.operator === SearchFilterEnum.In || filter.operator === SearchFilterEnum.NotIn) {
                    return (
                        <Select
                            mode="multiple"
                            allowClear
                            showSearch
                            filterOption={handleShowSearchFilterOption}
                            placeholder={placeholder}
                            options={filter.filterOptions || []}
                            value={filter.value as string[]}
                            onChange={(v) => updateFilter(filter.id, "value", v)}
                            className="w-full"
                            maxTagCount="responsive"
                        />
                    );
                }
                return (
                    <Select
                        allowClear
                        showSearch
                        filterOption={handleShowSearchFilterOption}
                        placeholder={placeholder}
                        options={filter.filterOptions || []}
                        value={filter.value as string | undefined}
                        onChange={(v) => updateFilter(filter.id, "value", v)}
                        className="w-full"
                    />
                );

            case FILTER_TYPES.boolean.type: {
                const booleanLabels = (filter.filterType as BooleanFilter).booleanLabels;
                return (
                    <Select
                        allowClear
                        placeholder={placeholder}
                        options={[
                            { label: booleanLabels?.true ?? filterTranslations.options.yes, value: true },
                            { label: booleanLabels?.false ?? filterTranslations.options.no, value: false },
                        ]}
                        value={filter.value as boolean | undefined}
                        onChange={(v) => updateFilter(filter.id, "value", v)}
                        className="w-full"
                    />
                );
            }

            // custom select filters
            case FILTER_TYPES.offerSelect.type:
                return (
                    <OfferSelectSearchable
                        mode="multiple"
                        allowClear
                        value={filter.value as string[]}
                        onChange={(v) => updateFilter(filter.id, "value", v)}
                        className="w-full"
                        maxTagCount="responsive"
                    />
                );

            case FILTER_TYPES.partnerSelect.type:
                return (
                    <PartnerSelectSearchable
                        mode="multiple"
                        allowClear
                        value={filter.value as string[]}
                        onChange={(v) => updateFilter(filter.id, "value", v)}
                        className="w-full"
                        maxTagCount="responsive"
                    />
                );

            case FILTER_TYPES.advertiserSelect.type:
                return (
                    <AdvertiserSelectSearchable
                        mode="multiple"
                        allowClear
                        value={filter.value as string[]}
                        onChange={(v) => updateFilter(filter.id, "value", v)}
                        className="w-full"
                        maxTagCount="responsive"
                    />
                );

            case FILTER_TYPES.cascadeSelect.type:
                return (
                    <CascadeSelectSearchable
                        mode="multiple"
                        allowClear
                        value={filter.value as string[]}
                        onChange={(v) => updateFilter(filter.id, "value", v)}
                        className="w-full"
                        maxTagCount="responsive"
                    />
                );

            case FILTER_TYPES.countrySelect.type:
                return (
                    <CountrySelectSearchable
                        allowClear
                        useNameAsValue
                        value={filter.value as string | undefined}
                        onChange={(v) => updateFilter(filter.id, "value", v)}
                        className="w-full"
                    />
                );

            case FILTER_TYPES.categorySelect.type:
                return (
                    <CategorySelectSearchable
                        allowClear
                        useNameAsValue
                        value={filter.value as string | undefined}
                        onChange={(v) => updateFilter(filter.id, "value", v)}
                        className="w-full"
                    />
                );

            case FILTER_TYPES.formStructureSelect.type:
                return (
                    <FormStructureSelectSearchable
                        allowClear
                        useSystemNameAsValue
                        value={filter.value as string | undefined}
                        onChange={(v) => updateFilter(filter.id, "value", v)}
                        className="w-full"
                    />
                );

            case FILTER_TYPES.stepTypeSelect.type:
                return (
                    <StepTypeSelectSearchable
                        allowClear
                        value={filter.value as string | undefined}
                        onChange={(v) => updateFilter(filter.id, "value", v)}
                        className="w-full"
                    />
                );

            case FILTER_TYPES.integrationStepTypeSelect.type:
                return (
                    <StepTypeSelectSearchable
                        allowClear
                        startContext="integration"
                        value={filter.value as string | undefined}
                        onChange={(v) => updateFilter(filter.id, "value", v)}
                        className="w-full"
                    />
                );

            case FILTER_TYPES.outgoingPostbackStepTypeSelect.type:
                return (
                    <StepTypeSelectSearchable
                        allowClear
                        startContext="outgoing_postback"
                        value={filter.value as string | undefined}
                        onChange={(v) => updateFilter(filter.id, "value", v)}
                        className="w-full"
                    />
                );

            case FILTER_TYPES.incomingPostbackStepTypeSelect.type:
                return (
                    <StepTypeSelectSearchable
                        allowClear
                        startContext="incoming_postback"
                        value={filter.value as string | undefined}
                        onChange={(v) => updateFilter(filter.id, "value", v)}
                        className="w-full"
                    />
                );

            case FILTER_TYPES.structureStepTypeSelect.type:
                return (
                    <StepTypeSelectSearchable
                        allowClear
                        startContext="structure"
                        value={filter.value as string | undefined}
                        onChange={(v) => updateFilter(filter.id, "value", v)}
                        className="w-full"
                    />
                );

            case FILTER_TYPES.conversionStateSourceSelect.type:
                return (
                    <Select
                        allowClear
                        showSearch
                        filterOption={handleShowSearchFilterOption}
                        placeholder={placeholder}
                        options={conversionSourceOptions}
                        value={filter.value as string | undefined}
                        onChange={(v) => updateFilter(filter.id, "value", v)}
                        className="w-full"
                    />
                );

            case FILTER_TYPES.conversionStateTypeSelect.type:
                return (
                    <Select
                        allowClear
                        showSearch
                        filterOption={handleShowSearchFilterOption}
                        placeholder={placeholder}
                        options={conversionTypeOptions || []}
                        value={filter.value as string | undefined}
                        onChange={(v) => updateFilter(filter.id, "value", v)}
                        className="w-full"
                    />
                );

            // custom multiselect filters
            case FILTER_TYPES.countryMultiSelect.type:
                return (
                    <CountrySelectSearchable
                        mode="multiple"
                        allowClear
                        useNameAsValue
                        value={filter.value as string[]}
                        onChange={(v) => updateFilter(filter.id, "value", v)}
                        className="w-full"
                        maxTagCount="responsive"
                    />
                );

            case FILTER_TYPES.conversionStateSourceMultiSelect.type:
                return (
                    <Select
                        mode="multiple"
                        allowClear
                        showSearch
                        filterOption={handleShowSearchFilterOption}
                        placeholder={placeholder}
                        options={conversionSourceOptions}
                        value={filter.value as unknown[]}
                        onChange={(v) => updateFilter(filter.id, "value", v)}
                        className="w-full"
                        maxTagCount="responsive"
                    />
                );

            case FILTER_TYPES.conversionStateTypeMultiSelect.type:
                return (
                    <Select
                        mode="multiple"
                        allowClear
                        showSearch
                        filterOption={handleShowSearchFilterOption}
                        placeholder={placeholder}
                        options={filter.filterOptions?.length ? filter.filterOptions : conversionTypeOptions || []}
                        value={filter.value as unknown[]}
                        onChange={(v) => updateFilter(filter.id, "value", v)}
                        className="w-full"
                        maxTagCount="responsive"
                    />
                );

            case FILTER_TYPES.conversionStateStatusMultiSelect.type:
                return (
                    <Select
                        mode="multiple"
                        allowClear
                        showSearch
                        filterOption={handleShowSearchFilterOption}
                        placeholder={placeholder}
                        options={filter.filterOptions || []}
                        value={filter.value as unknown[]}
                        onChange={(v) => updateFilter(filter.id, "value", v)}
                        className="w-full"
                        maxTagCount="responsive"
                    />
                );

            case FILTER_TYPES.labelMultiSelect.type:
                return (
                    <LabelSelectSearchable
                        mode="multiple"
                        allowClear
                        useNameAsValue
                        value={filter.value as string[]}
                        onChange={(v) => updateFilter(filter.id, "value", v)}
                        className="w-full"
                        maxTagCount="responsive"
                    />
                );

            default:
                return null;
        }
    };

    return (
        <Row gutter={[8, 8]} align="middle">
            <Col xs={24} md={6}>
                <Select
                    className="w-full min-w-0"
                    showSearch
                    filterOption={handleShowSearchFilterOption}
                    options={availableFilterColumns.map((col) => ({
                        label: isValidElement(col.title)
                            ? ((col.title.props as { columnTitle?: string }).columnTitle ?? "")
                            : String(col.title ?? ""),
                        value: col.dataIndex as string,
                    }))}
                    value={filter.dataIndex}
                    onChange={(v) => updateFilter(filter.id, "dataIndex", v)}
                />
            </Col>

            <Col xs={24} md={6}>
                <Select
                    className="w-full min-w-0"
                    showSearch
                    filterOption={handleShowSearchFilterOption}
                    options={filter.filterType.operators.map((op) => ({
                        label: op === SearchFilterEnum.Null && filter.filterNullOptionLabel
                            ? filter.filterNullOptionLabel
                            : isEntitySelect
                            ? (filterTranslations.entityOperators[op as SearchFilterEnum.In | SearchFilterEnum.NotIn] ?? op)
                            : (filterTranslations.operators[op] ?? op),
                        value: op,
                    }))}
                    value={filter.operator}
                    onChange={(v) => {
                        updateFilter(filter.id, "operator", v);
                        const wasRange =
                            filter.operator === SearchFilterEnum.Between ||
                            filter.operator === SearchFilterEnum.NotBetween;
                        const isRange = v === SearchFilterEnum.Between || v === SearchFilterEnum.NotBetween;
                        const wasMulti =
                            filter.operator === SearchFilterEnum.In || filter.operator === SearchFilterEnum.NotIn;
                        const isMulti = v === SearchFilterEnum.In || v === SearchFilterEnum.NotIn;
                        const wasValueless =
                            filter.operator === SearchFilterEnum.Null || filter.operator === SearchFilterEnum.NotNull;
                        const isValueless = v === SearchFilterEnum.Null || v === SearchFilterEnum.NotNull;

                        // Reset value when switching between operators with different value shapes.
                        if (wasRange !== isRange || wasMulti !== isMulti || wasValueless !== isValueless) {
                            let newVal: unknown;
                            if (isValueless) newVal = null;
                            else if (isRange) newVal = [null, null];
                            else if (isMulti) newVal = [];
                            else if (filter.filterType.type === FILTER_TYPES.number.type) newVal = 0;
                            else if (filter.filterType.type === FILTER_TYPES.boolean.type) newVal = undefined;
                            else if (filter.filterType.type === FILTER_TYPES.string.type) newVal = "";
                            else newVal = null;
                            updateFilter(filter.id, "value", newVal);
                        }
                    }}
                />
            </Col>

            <Col xs={24} md={12}>
                <div style={{ display: "flex", gap: 8, alignItems: "center", minWidth: 0 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        {/* ensure inner controls use className="w-full min-w-0" */}
                        {renderValueInput()}
                    </div>
                    <Button
                        type="text"
                        icon={<DeleteOutlined />}
                        onClick={() => removeFilter(filter.id)}
                        aria-label="Remove filter"
                    />
                </div>
            </Col>
        </Row>
    );
};
